import React, { useContext, useState, useEffect } from 'react';
import { CartContext } from '../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import styles from './Cart.module.css';

const Cart = () => {
    const { cartItems, removeFromCart } = useContext(CartContext);
    const [isOpen, setIsOpen] = useState(false); // Toggle cart sidebar
    const [totalPrice, setTotalPrice] = useState(0);
    const [discountedPrice, setDiscountedPrice] = useState(0);
    const [discountPercentage, setDiscountPercentage] = useState(0);

    const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);

    const handleCartToggle = () => {
        setIsOpen(!isOpen);
    };

    const navigate = useNavigate();

    const handleCheckout = () => {
        setIsOpen(false);
        navigate('/order');
    };

    const handleContinueShopping = () => {
        setIsOpen(false);
        navigate('/products');
    };

    // Calculate the total price and discounted price whenever cartItems change
    useEffect(() => {
        const total = cartItems.reduce((sum, item) => {
            return sum + (parseFloat(item.discount_price.replace(/[^\d.-]/g, '')) * item.quantity);
        }, 0);

        let discount = 0;

        if (totalItems === 2) {
            discount = 0.10; // 10% discount
        } else if (totalItems >= 3) {
            discount = 0.15; // 15% discount
        }

        const discountedTotal = total - (total * discount);

        setTotalPrice(total.toFixed(2));
        setDiscountedPrice(discountedTotal.toFixed(2));
        setDiscountPercentage((discount * 100).toFixed(0)); // Set discount percentage
    }, [cartItems, totalItems]);

    return (
        <>
            <button className={styles.cartToggleButton} onClick={handleCartToggle}>
                <i className="fas fa-shopping-cart"></i>
                <span className={styles.cartCount}>{totalItems}</span> 
            </button>

            <div className={`${styles.cartSidebar} ${isOpen ? styles.cartSidebarOpen : styles.cartSidebarClosed}`}>
                <div className={styles.cartHeader}>
                    <div className={styles.cartHeadingGroup}>
                        <p className={styles.cartEyebrow}>Моята количка</p>
                        <h3>Детайли по поръчка</h3>
                    </div>
                    <button className={styles.closeCart} onClick={handleCartToggle}>
                        ×
                    </button>
                </div>
                <div className={styles.cartContent}>
                    {cartItems.length === 0 ? (
                        <div className={styles.emptyState}>
                            <i className="fas fa-shopping-bag"></i>
                            <p>Количката е празна.</p>
                            <button className={styles.secondaryButton} onClick={handleContinueShopping}>
                                Към продуктите
                            </button>
                        </div>
                    ) : (
                        <ul className={styles.cartItemsList}>
                            {cartItems.map(item => {
                                const itemPrice = parseFloat(item.discount_price.replace(/[^\d.-]/g, '')) || 0;
                                const lineTotal = (itemPrice * item.quantity).toFixed(2);
                                const primaryImage =
                                    item.images?.find((image) => image.is_primary)?.image_url ||
                                    item.images?.[0]?.image_url ||
                                    "/images/placeholder.png";

                                return (
                                    <li key={`${item.id}-${item.option || "default"}`} className={styles.cartItem}>
                                        <div className={styles.itemThumb}>
                                            <img src={primaryImage} alt={item.productname} />
                                        </div>
                                        <div className={styles.itemInfo}>
                                            <div className={styles.itemTopRow}>
                                                <span className={styles.itemName}>{item.productname}</span>
                                                <button className={styles.removeItemFromCart} onClick={() => removeFromCart(item.id)}>Премахни</button>
                                            </div>
                                            <div className={styles.itemMeta}>
                                                <span className={styles.itemQuantity}>Количество: {item.quantity}</span>
                                                <span className={styles.itemPrice}>{lineTotal} €</span>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                {cartItems.length > 0 && (
                    <div className={styles.summaryCard}>
                        <div className={styles.totalPrice}>
                            {totalPrice !== discountedPrice ? (
                                <>
                                    <div className={styles.totalRow}>
                                        <span>Междинна сума</span>
                                        <span className={styles.cartOldPrice}>{totalPrice} €.</span>
                                    </div>
                                    <div className={styles.totalRow}>
                                        <span>Сума след отстъпка</span>
                                        <span className={styles.cartNewPrice}>{discountedPrice} €</span>
                                    </div>
                                    <div className={styles.totalRow}>
                                        <span className={styles.cartDiscountText}>Отстъпка</span>
                                        <span className={styles.cartDiscountText}>{discountPercentage}%</span>
                                    </div>
                                </>
                            ) : (
                                <div className={styles.totalRow}>
                                    <span>Междинна сума</span>
                                    <span className={styles.cartNewPrice}>{totalPrice} €</span>
                                </div>
                            )}
                        </div>

                        {totalItems < 3 && (
                            <div className={styles.discountSuggestionBox}>
                                <i className="fas fa-gift"></i>
                                {totalItems === 1 ? (
                                    <p>Добави още 1 продукт за 10% отстъпка или общо 2 за 15%.</p>
                                ) : (
                                    <p>Добави още 1 продукт и вземи 15% отстъпка.</p>
                                )}
                            </div>
                        )}

                        <button className={styles.checkoutButton} onClick={handleCheckout}>Към завършване</button>
                        <button className={styles.secondaryButton} onClick={handleContinueShopping}>Продължи пазаруването</button>
                    </div>
                )}
            </div>

            {isOpen && <div className={styles.overlay} onClick={handleCartToggle}></div>}
        </>
    );
};

export default Cart;
